import React from "react";
import { AiChatSourceReference } from "../AiChat.types";

// Backend emits 【N】 (U+3010/U+3011), but the LLM sometimes rewrites the brackets to
// 〔N〕 (U+3014/U+3015) on its way out — so we accept either pair. Mismatched pairs are tolerated.
const citationRegex = /[【〔](\d+(?:\s*,\s*\d+)*)[】〕]/g;

export function splitCitations(
    text: string,
    sources: AiChatSourceReference[],
    createChip: (source: AiChatSourceReference, index: number) => React.ReactNode
): React.ReactNode[] {
    const result: React.ReactNode[] = [];
    let lastIndex = 0;

    let match;
    citationRegex.lastIndex = 0;
    while ((match = citationRegex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            result.push(text.slice(lastIndex, match.index));
        }

        const sourceIndices = match[1].split(",").map((n) => parseInt(n.trim(), 10) - 1);
        const validIndices = sourceIndices.filter((i) => i >= 0 && i < sources.length);

        if (validIndices.length > 0) {
            validIndices.forEach((sourceIndex) => {
                result.push(createChip(sources[sourceIndex], sourceIndex));
            });
        } else {
            result.push(match[0]);
        }

        lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
        result.push(text.slice(lastIndex));
    }

    return result;
}

export function processChildren(
    children: React.ReactNode,
    sources: AiChatSourceReference[],
    createChip: (source: AiChatSourceReference, index: number) => React.ReactNode
): React.ReactNode {
    return React.Children.map(children, (child) => {
        if (typeof child === "string") {
            const parts = splitCitations(child, sources, createChip);
            return parts.length === 1 ? parts[0] : React.createElement(React.Fragment, null, ...parts);
        }
        if (React.isValidElement(child) && child.props.children) {
            return React.cloneElement(child, {}, processChildren(child.props.children, sources, createChip));
        }
        return child;
    });
}
